import type { PackageWithFeatureCodes } from "../../api/packages.service";
import { PackageCard } from "../PackageCard";
import styles from "./PackageGrid.module.css";

type PackageGridProps = {
  packages: PackageWithFeatureCodes[];
  onPackageClick: (pkg: PackageWithFeatureCodes) => void;
  loading?: boolean;
};

function SkeletonCard() {
  return (
    <div className={styles.card} aria-hidden="true">
      <div className={styles.skeletonHeader}>
        <div className={styles.skeletonTitle} />
        <div className={styles.skeletonBadge} />
      </div>
      <div className={styles.skeletonText} />
      <div className={styles.skeletonText} />
      <div className={styles.skeletonFooter}>
        <div className={styles.skeletonChip} />
        <div className={styles.skeletonChip} />
      </div>
    </div>
  );
}

function SectionEmptyState({ message }: { message: string }) {
  return (
    <div className={styles.sectionEmpty}>
      <p className={styles.emptyText}>{message}</p>
    </div>
  );
}

function PackageSection({
  title,
  packages,
  onPackageClick,
}: {
  title: string;
  packages: PackageWithFeatureCodes[];
  onPackageClick: (pkg: PackageWithFeatureCodes) => void;
}) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionHeading}>{title}</h2>
      {packages.length === 0 ? (
        <SectionEmptyState message={`No ${title.toLowerCase()}`} />
      ) : (
        <div className={styles.grid}>
          {packages.map(pkg => (
            <PackageCard key={pkg.id} package={pkg} onClick={() => onPackageClick(pkg)} />
          ))}
        </div>
      )}
    </section>
  );
}

export default function PackageGrid({
  packages,
  onPackageClick,
  loading = false,
}: PackageGridProps) {
  if (loading) {
    return (
      <div className={styles.container}>
        <section className={styles.section}>
          <h2 className={styles.sectionHeading}>Product Packages</h2>
          <div className={styles.grid}>
            {Array.from({ length: 4 }).map((_, skeletonIndex) => (
              <SkeletonCard key={`skeleton-${skeletonIndex}`} />
            ))}
          </div>
        </section>
      </div>
    );
  }

  const defaultPackages = packages.filter(pkg => pkg.type === "default");
  const productPackages = packages.filter(pkg => pkg.type !== "default");

  return (
    <div className={styles.container}>
      <PackageSection
        title="Default Packages"
        packages={defaultPackages}
        onPackageClick={onPackageClick}
      />
      <PackageSection
        title="Product Packages"
        packages={productPackages}
        onPackageClick={onPackageClick}
      />
    </div>
  );
}
